import { useRef, useCallback, useEffect } from 'react';

import { getAIAgentMessages, getAIAgentThreads } from '../api';
import type { AIAgentMessage } from '../types';

const POLL_INTERVAL = 1000; // 1 second
const MAX_POLL_ATTEMPTS = 60; // 60 seconds max

type PollingCallbacks = {
  onMessagesReceived: (messages: AIAgentMessage[]) => void;
  onAssistantResponse: () => void;
  onError: (message: string) => void;
  onComplete: () => void;
};

/**
 * Hook for polling AI Agent responses.
 * Handles the polling loop with exponential backoff and thread status checking.
 */
export const useAIAgentPolling = (callbacks: PollingCallbacks) => {
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollAttemptsRef = useRef(0);
  const lastMessageIdRef = useRef<number | null>(null);
  const callbacksRef = useRef(callbacks);

  // Keep callbacks ref updated
  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  const stopPolling = useCallback(() => {
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
      pollingTimeoutRef.current = null;
    }
    pollAttemptsRef.current = 0;
  }, []);

  const pollForResponse = useCallback(async(threadId: number) => {
    try {
      // Get messages and thread status in parallel
      const [messagesResponse, threadsResponse] = await Promise.all([
        getAIAgentMessages(threadId),
        getAIAgentThreads(),
      ]);

      // Find the current thread to check is_processing status
      const currentThread = threadsResponse.results.find((t) => t.id === threadId);

      console.info(
        `[AIAgent] Poll attempt ${pollAttemptsRef.current}: found ${messagesResponse.results.length} messages, ` +
        `lastMessageId=${lastMessageIdRef.current}, is_processing=${currentThread?.is_processing}`,
      );

      if (messagesResponse.results.length === 0) {
        // No messages yet, continue polling
        pollAttemptsRef.current += 1;
        if (pollAttemptsRef.current < MAX_POLL_ATTEMPTS) {
          pollingTimeoutRef.current = setTimeout(() => pollForResponse(threadId), POLL_INTERVAL);
        } else {
          console.error('[AIAgent] Max poll attempts reached');
          callbacksRef.current.onError('AI Agent took too long to respond. Please try again.');
          callbacksRef.current.onComplete();
          pollAttemptsRef.current = 0;
        }
        return;
      }

      // Update messages with latest data
      callbacksRef.current.onMessagesReceived(messagesResponse.results);

      // Find any assistant message with ID greater than the last message we sent
      const newAssistantMessage = messagesResponse.results.find(
        (msg) => msg.role === 'assistant' && msg.id > (lastMessageIdRef.current || 0),
      );

      console.info('[AIAgent] Looking for assistant message with id >', lastMessageIdRef.current, 'Found:', newAssistantMessage?.id);

      if (newAssistantMessage) {
        // Found new assistant response, stop polling
        console.info('[AIAgent] Assistant response received, stopping polling');
        lastMessageIdRef.current = messagesResponse.results[0].id;
        callbacksRef.current.onAssistantResponse();
        callbacksRef.current.onComplete();
        pollAttemptsRef.current = 0;
      } else if (currentThread && !currentThread.is_processing && pollAttemptsRef.current > 3) {
        // Backend finished processing but no assistant message - this is an error
        console.error('[AIAgent] Backend finished processing but no assistant response found');
        callbacksRef.current.onError('AI Agent failed to generate a response. Please try again.');
        callbacksRef.current.onComplete();
        pollAttemptsRef.current = 0;
      } else {
        // No new assistant message yet, continue polling
        pollAttemptsRef.current += 1;

        if (pollAttemptsRef.current < MAX_POLL_ATTEMPTS) {
          pollingTimeoutRef.current = setTimeout(() => pollForResponse(threadId), POLL_INTERVAL);
        } else {
          console.error('[AIAgent] Max poll attempts reached');
          callbacksRef.current.onError('AI Agent took too long to respond. Please try again.');
          callbacksRef.current.onComplete();
          pollAttemptsRef.current = 0;
        }
      }
    } catch (err) {
      console.error('[AIAgent] Error polling for messages:', err);
      callbacksRef.current.onError('Failed to get AI response. Please try again.');
      callbacksRef.current.onComplete();
      pollAttemptsRef.current = 0;
    }
  }, []);

  const startPolling = useCallback((threadId: number) => {
    stopPolling();
    pollAttemptsRef.current = 0;
    pollForResponse(threadId);
  }, [pollForResponse, stopPolling]);

  const setLastMessageId = useCallback((id: number | null) => {
    lastMessageIdRef.current = id;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    startPolling,
    stopPolling,
    setLastMessageId,
  };
};
